import { useEffect, useState } from "react";
import { CONFIG } from "src/config";

/** How often a tab that stays open re-checks. Frequent enough to catch a deploy
 *  inside a working session, rare enough to be invisible — one request for a
 *  file of about a hundred bytes. */
const POLL_INTERVAL_MS = 15 * 60 * 1000;

async function fetchDeployedBuildId(): Promise<string | null> {
	try {
		// Both the cache-busting query and `no-store`: between the browser, the
		// Firebase CDN and whatever proxy sits in the middle, one of them will
		// otherwise happily answer with the very file we are checking for change.
		const response = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
		if (!response.ok) return null;
		const data = (await response.json()) as { buildId?: string };
		return data.buildId ?? null;
	} catch {
		// Offline, or a deploy swapping files underneath us. Not knowing is fine;
		// the next check answers. This must never surface to the user — a failed
		// version check is not something anyone can act on.
		return null;
	}
}

/**
 * Reports whether hosting is serving a newer build than the one this tab is
 * running.
 *
 * A tab only reads index.html once, when it opens. Everything after that runs
 * from the bundle it loaded then, so a deploy is completely invisible to it —
 * it has to go and ask. That is what this does: compare the build id baked into
 * this bundle against the one in the deployed version.json.
 *
 * @param enabled gate for whether to check at all.
 * @returns whether this tab is stale, and the build id hosting serves now.
 */
export function useVersionCheck(enabled: boolean): { updateAvailable: boolean; deployedBuildId: string | null } {
	const [deployedBuildId, setDeployedBuildId] = useState<string | null>(null);
	const updateAvailable = deployedBuildId !== null;

	useEffect(() => {
		// The dev server serves no version.json, and a developer restarting Vite
		// is not a new version worth announcing.
		// Once stale, stop asking: the answer cannot change back.
		if (!enabled || __VITE_DEV_SERVER__ || updateAvailable) return;

		let cancelled = false;

		const check = async () => {
			const deployed = await fetchDeployedBuildId();
			if (cancelled || !deployed) return;
			if (deployed !== CONFIG.BUILD_ID) setDeployedBuildId(deployed);
		};

		check();

		// The check that actually matters in practice: an admin leaves the tab
		// open all day and comes back to it after a deploy has landed.
		const onVisibilityChange = () => {
			if (document.visibilityState === "visible") check();
		};
		document.addEventListener("visibilitychange", onVisibilityChange);
		const timer = window.setInterval(check, POLL_INTERVAL_MS);

		return () => {
			cancelled = true;
			document.removeEventListener("visibilitychange", onVisibilityChange);
			window.clearInterval(timer);
		};
	}, [enabled, updateAvailable]);

	return { updateAvailable, deployedBuildId };
}
